import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A popup that opens after the current product is added to the cart, offering
 * related "lichtbron" (light source) products to add.
 *
 * Behaviour (per merchant request):
 *  - When the current product is added: show only this popup, suppress the cart drawer.
 *  - When a light source is added from within the popup: close the popup and open the
 *    cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class LichtbronPopupComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartAdd = (event) => {
    const data = event.detail?.data ?? {};
    const { dialog } = this.refs;
    const drawer = document.querySelector('cart-drawer-component');

    if (data.didError) return;

    if (data.productId === this.dataset.productId && !dialog.open) {
      // The current product was added. Defer past the cart drawer's auto-open
      // (it schedules its own requestAnimationFrame) so we end up on top and the
      // drawer is closed.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          drawer?.close?.();
          this.showDialog();
        })
      );
    } else if (dialog.open) {
      // A different product (a light source from the popup) was added while the
      // popup is open. Close the popup and reveal the cart drawer.
      this.closeDialog().then(() => drawer?.open?.());
    }
  };
}

if (!customElements.get('lichtbron-popup-component')) {
  customElements.define('lichtbron-popup-component', LichtbronPopupComponent);
}
